import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, SlidersHorizontal } from "lucide-react";
import api from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/common/PageHeader";
import { ProjectScopeBanner } from "../components/common/ProjectScopeBanner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";

function LimitRow({ row, projectId, canManage }) {
  const [qtyOn, setQtyOn] = useState(row.qty_enabled);
  const [maxQty, setMaxQty] = useState(row.max_qty || "");
  const [daysOn, setDaysOn] = useState(row.days_enabled);
  const [maxDays, setMaxDays] = useState(row.max_days || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (qtyOn && (!maxQty || Number(maxQty) < 1)) { toast.error("Enter a max quantity ≥ 1"); return; }
    if (daysOn && (!maxDays || Number(maxDays) < 1)) { toast.error("Enter max days ≥ 1"); return; }
    setSaving(true);
    try {
      await api.put("/allocation-limits", {
        project_id: projectId, material_type_id: row.material_type_id,
        qty_enabled: qtyOn, max_qty: Number(maxQty) || 0,
        days_enabled: daysOn, max_days: Number(maxDays) || 0,
      });
      toast.success(`Saved limits for ${row.type_name}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <tr className="border-b border-slate-100 last:border-0" data-testid={`al-row-${row.material_type_id}`}>
      <td className="px-4 py-3 text-sm font-medium text-slate-800">{row.type_name}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Checkbox data-testid={`al-qty-toggle-${row.material_type_id}`} checked={qtyOn} disabled={!canManage} onCheckedChange={(v) => setQtyOn(!!v)} />
          <Input data-testid={`al-qty-input-${row.material_type_id}`} type="number" min="1" value={maxQty} disabled={!canManage || !qtyOn}
            onChange={(e) => setMaxQty(e.target.value)} placeholder="Max units" className="h-8 w-28" />
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Checkbox data-testid={`al-days-toggle-${row.material_type_id}`} checked={daysOn} disabled={!canManage} onCheckedChange={(v) => setDaysOn(!!v)} />
          <Input data-testid={`al-days-input-${row.material_type_id}`} type="number" min="1" value={maxDays} disabled={!canManage || !daysOn}
            onChange={(e) => setMaxDays(e.target.value)} placeholder="Max days" className="h-8 w-28" />
        </div>
      </td>
      <td className="px-4 py-3 text-right">
        {canManage && (
          <Button data-testid={`al-save-${row.material_type_id}`} size="sm" variant="outline" disabled={saving} onClick={save}>
            {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />} Save
          </Button>
        )}
      </td>
    </tr>
  );
}

export default function AllocationLimits() {
  const { ctx } = useWorkspace();
  const { has } = useAuth();
  const projectId = ctx.project?.id || "";
  const canManage = has("ALLOCATION_LIMIT.MANAGE");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) { setRows([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/allocation-limits", { params: { project_id: projectId } });
      setRows(data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load limits"); }
    finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <PageHeader title="Field Allocation Limits" subtitle="Cap how much (and how long) a field user can hold materials pending, per material type" />
      <ProjectScopeBanner requireProject action="allocation limits" />
      {projectId && (
        <>
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-[var(--mf-border)] bg-[var(--mf-surface)] p-3 text-xs text-slate-600" data-testid="al-help">
            <SlidersHorizontal className="mt-0.5 h-4 w-4 shrink-0 text-[var(--mf-purple-600)]" />
            <div>
              <b>Quantity cap</b>: block issuing more of a type once the field user already holds that many in <i>Pending Acknowledgement + Acknowledged</i> stage for this project.
              <br /><b>Ageing cap</b>: block issuing more of a type when the field user has one older than the set number of days in those stages. Leave a rule unchecked to keep it off.
            </div>
          </div>
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100/80">
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Material Type</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Quantity Cap (max pending units)</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Ageing Cap (max days pending)</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody data-testid="al-rows">
                {loading ? <tr><td colSpan={4} className="py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></td></tr>
                  : rows.length === 0 ? <tr><td colSpan={4} className="py-10 text-center text-sm text-slate-400">No active material types for this project</td></tr>
                  : rows.map((r) => <LimitRow key={r.material_type_id} row={r} projectId={projectId} canManage={canManage} />)}
              </tbody>
            </table>
          </div>
          {!canManage && <p className="mt-2 text-xs text-slate-400">You have view-only access. The Allocation Limits Manage permission is required to change these values.</p>}
        </>
      )}
    </div>
  );
}
